/// <amd-module name="Widgets/SprintOverviewWidget/NColorBarControl" />

import * as React from "react";
import { VssComponent } from "VSS/Platform/Layout";

export interface NColorBarCounts {
    /**
     * Number asigned to the bar
     */
    count: number;
    /**
     * Hover of the bar
     */
    hover: string;
    /**
     * Color of the bar
     */
    color: string;
}

interface NColorBarSection {
    /**
     * index of bar section
     */
    index: number;
    /**
     * Value of bar section
     */
    value: number;
    /**
     * percentage of bar section
     */
    pct: number;
    /**
     * hover of bar section
     */
    hover: string;
    /**
     * text in bar section
     */
    text?: string;
    /**
     * color of bar section
     */
    color: string;
}

export interface NColorBarControlOptions {
    counts: NColorBarCounts[];
    size: number;
}

export class NColorBarControl extends VssComponent<NColorBarControlOptions> {
    public static DomCoreCssClass: string = "widget-shared-ncolor-bar-control";

    private _countTotal: number = 0;
    private _counts: NColorBarSection[] = [];
    private _size: number;

    constructor(props: NColorBarControlOptions) {
        super(props);
        this._size = props.size;
        this.calculateBarPercentage(props.counts);
    }

    public UNSAFE_componentWillUpdate(nextProps: NColorBarControlOptions) {
        this.calculateBarPercentage(nextProps.counts);
    }

    public render(): JSX.Element {
        return (
            <div className={NColorBarControl.DomCoreCssClass}>
                <div className="visual-progress-container">{this.getSections()}</div>
            </div>
        );
    }

    private calculateBarPercentage(counts: NColorBarCounts[]): void {
        this._counts = [];
        this._countTotal = 0;
        const length: number = counts.length;
        let percentageSum = 0;

        for (let i = 0; i < length; i++) {
            // get the value equals to 100%
            this._countTotal += counts[i].count;
        }
        for (let i = 0; i < length; i++) {
            // get the percentage value for each element
            if (this._countTotal !== 0) {
                this._counts.push({
                    index: i,
                    value: counts[i].count,
                    pct: (counts[i].count * 100) / this._countTotal,
                    hover: counts[i].hover,
                    color: counts[i].color
                });
            } else {
                this._counts.push({
                    index: i,
                    value: counts[i].count,
                    pct: 0,
                    hover: counts[i].hover,
                    color: counts[i].color
                });
            }
            // for each element get his size
            if (i < length - 1) {
                this._counts[i].pct = Math.floor(this._counts[i].pct * 100) / 100;
                percentageSum += this._counts[i].pct;
            }
        }
        // for the last element we calculate using the remaining part so it doesnt go out of bounds
        this._counts[this._counts.length - 1].pct = 100 - percentageSum;
    }

    private textFits(value: number, percentage: number): boolean {
        // check that text in the bar dont get out of bands
        let digits = 2; // we start digits in 2 because the margin equals the space of 1 plus 1 digit we always have
        let digitPercentageWidth: number = (6.5 * 100) / this._size; // the size of a digit is 6.5 pixels. We calculate the equivalent size for that in the bar percentage
        digitPercentageWidth += 0.3; // add an epsilon to avoid going out of bounds
        while (value / 10 > 1) {
            // if we can still divide between 10 it means we have more digits
            value /= 10;
            digits++;
        }
        return digits * digitPercentageWidth <= percentage; // a digit size is equivalent to 2.1% se we check the text fits
    }

    private getSections(): JSX.Element {
        const sections: JSX.Element[] = [];

        for (let i = 0; i < this._counts.length; i++) {
            // for each bar selects his color and text
            const section: NColorBarSection = this._counts[i];
            const cssContainerClass = "visual-progress-" + section.color;
            const cssCountClass = "visual-progress-count";
            const hasData: boolean =
                (section.value > 0 || section.text !== undefined) &&
                this.textFits(section.value, section.pct) &&
                section.index !== this._counts.length - 1;
            const countSpan = <span className={cssCountClass}>{hasData ? (section.text ? section.text : section.value) : ""}</span>;

            const style: React.CSSProperties = {
                width: section.pct + "%"
            };

            if (section.pct === 0) {
                style["display"] = "none";
            }

            sections.push(
                <div key={i} className={cssContainerClass} style={style} aria-label={section.hover}>
                    {countSpan}
                </div>
            );
        }

        return <>{...sections}</>;
    }
}
